import * as React from 'react';
import { connect } from 'react-redux';
import { Layout, Spin, Row, Col, Card, Table } from 'antd';

import {
	collectAllParentsFromTreeData,
	filterTreeData,
	getCurrency,
	nullSafeGet,
	nullSafeGetOrElse,
	removeEmptyChildrenFromTreeData,
	sortTreeData,
} from '../../utils/DataAccessUtils';
import { applyLocalAnalyticsTreeFilters } from '../../utils/AnalyticsTreeFilterUtils';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import { withRouter } from 'react-router';
import AnalyticsControlBar from '../analytics_control_bar/AnalyticsControlBar';
import {
	clearProblemTypeFilters,
	updateProblemTypeFilters,
} from '../../actions/spend_analytics_actions';
import {
	fetchSpendCostsByMonth,
	fetchSpendCostsByProblemTypeWithChildren,
} from '../../thunks/spend_reportings_thunks';
import { debounce } from '../../utils/PerformanceUtils';
import { EmptyState } from '../empty_state/EmptyState';
import { SpendOverTimeChart } from '../spend_analytics_overview_page/SpendOverTimeChart';
import { SpendBreakdownSection } from '../spend_analytics_overview_page/SpendBreakdownSection';
import moment from 'moment';
import {
	flattenSpendCosts,
	getActiveSpendSources,
	SPEND_SOURCES,
} from '../../utils/AnalyticsUtils';
import { buildSpendBreakdownColumns } from '../spend_analytics_overview_page/SpendBreakdownColumns';
import { exportSpendBySourceCsv } from '../../utils/AnalyticsBreakdownExportUtils';
import { FILTER_CONFIG_NAMES } from '../../utils/DataConstants';
import { withUserType } from '../withUserType';
import { exportAnalyticsPageAsPdf } from '../../utils/AnalyticsPdfExportUtils';

require('./SpendAnalyticsOverviewProblemTypesPage.less');
const { Content } = Layout;
const isEqual = require('fast-deep-equal');

const CSV_TITLE = 'Spend Analytics by Problem Types';

class SpendAnalyticsOverviewProblemTypesPage extends React.Component<any, any> {
	skipNextDebounce = false;

	state = {
		initialLoading: true,
		search: '',
		filteredTreeData: [],
		problemTypeIds: [],
		printing: false,
	};
	debouncedCallback = debounce(
		() => {
			this.syncDateFiltersToUrl();
			const { fetchSpendData, fetchTimeSeriesSpendData, analytics } = this.props;
			const { initialLoading } = this.state;
			const promise1 = fetchSpendData(analytics.problemTypeFilters);
			const promise2 = fetchTimeSeriesSpendData(analytics.problemTypeFilters);
			if (initialLoading) {
				Promise.all([promise1, promise2]).then(() => this.setState({ initialLoading: false }));
			}
		},
		500,
		false
	);

	// Expand all tree rows + drop pagination, let the table re-render, then capture so the PDF
	// includes every row.
	handlePrint = () =>
		this.setState({ printing: true }, () =>
			setTimeout(
				() =>
					exportAnalyticsPageAsPdf(
						'.analyticsOverviewProblemTypesPage',
						'spend-analytics-problem-types-' + new Date().toISOString().slice(0, 10) + '.pdf'
					).finally(() => this.setState({ printing: false })),
				400
			)
		);

	handleExportCsv = () => {
		const { currentUser } = this.props;
		const currency = getCurrency({ currentUser });
		const fieldRows = flattenSpendCosts(this.getDisplayData());
		exportSpendBySourceCsv({
			title: CSV_TITLE,
			categoryColumnLabel: 'Problem Type',
			rows: fieldRows,
			activeSources: getActiveSpendSources(fieldRows),
			formatValue: (amount) => currency.symbolFormat(amount, 0),
		});
	};

	componentDidMount() {
		const {
			fetchSpendData,
			fetchTimeSeriesSpendData,
			analytics,
			location,
			saveProblemTypeFilters,
		} = this.props;
		const { initialLoading } = this.state;
		const searchParams = new URLSearchParams((location && location.search) || '');
		const startDateParam = searchParams.get('startDate');
		const endDateParam = searchParams.get('endDate');

		if (startDateParam && endDateParam) {
			const startDate = moment(startDateParam, 'YYYY-MM-DD');
			const endDate = moment(endDateParam, 'YYYY-MM-DD');
			const hasValidStartAndEndDate = startDate.isValid() && endDate.isValid();
			if (hasValidStartAndEndDate) {
				const urlFilters = {
					...analytics.problemTypeFilters,
					startDate,
					endDate,
				};
				// Avoid duplicate API call from componentDidUpdate after URL-init filter hydration.
				this.skipNextDebounce = true;
				saveProblemTypeFilters(urlFilters);
				const promise1 = fetchSpendData(urlFilters);
				const promise2 = fetchTimeSeriesSpendData(urlFilters);
				if (initialLoading) {
					Promise.all([promise1, promise2]).then(() => this.setState({ initialLoading: false }));
				}
				return;
			}
		}

		if (analytics.problemTypeFilters.startDate && analytics.problemTypeFilters.endDate) {
			const promise1 = fetchSpendData(analytics.problemTypeFilters);
			const promise2 = fetchTimeSeriesSpendData(analytics.problemTypeFilters);
			if (initialLoading) {
				Promise.all([promise1, promise2]).then(() => this.setState({ initialLoading: false }));
			}
		}
	}

	syncDateFiltersToUrl = () => {
		const { history, location, analytics } = this.props;
		const startDate = nullSafeGet('problemTypeFilters.startDate', analytics);
		const endDate = nullSafeGet('problemTypeFilters.endDate', analytics);
		const searchParams = new URLSearchParams((location && location.search) || '');

		const hasValidStartDate = startDate && moment(startDate).isValid();
		const hasValidEndDate = endDate && moment(endDate).isValid();

		if (hasValidStartDate && hasValidEndDate) {
			searchParams.set('startDate', moment(startDate).format('YYYY-MM-DD'));
			searchParams.set('endDate', moment(endDate).format('YYYY-MM-DD'));
		} else {
			searchParams.delete('startDate');
			searchParams.delete('endDate');
		}

		const nextSearch = searchParams.toString();
		const currentSearch = ((location && location.search) || '').replace(/^\?/, '');
		if (nextSearch !== currentSearch) {
			history.replace({
				pathname: location.pathname,
				search: nextSearch ? `?${nextSearch}` : '',
			});
		}
	};

	componentDidUpdate(prevProps) {
		const { analytics } = this.props;
		if (!isEqual(prevProps.analytics.problemTypeFilters, analytics.problemTypeFilters)) {
			if (this.skipNextDebounce) {
				this.skipNextDebounce = false;
				this.syncDateFiltersToUrl();
				return;
			}
			this.debouncedCallback();
		}
	}

	filterByProblemTypeId = (allData) =>
		applyLocalAnalyticsTreeFilters(allData, {
			selectedIds: nullSafeGetOrElse('state.problemTypeIds', this, []),
		});

	getDisplayData = () => {
		const spendByProblemType = nullSafeGetOrElse(
			'props.analytics.spendCostsByProblemType',
			this,
			[]
		);

		const displayData = !!this.state.search
			? nullSafeGetOrElse('state.filteredTreeData', this, [])
			: this.filterByProblemTypeId(nullSafeGetOrElse('data', spendByProblemType, []));
		return sortTreeData(
			displayData,
			(a, b) =>
				parseFloat(nullSafeGetOrElse('spendCosts.totalSpend', b, 0)) -
				parseFloat(nullSafeGetOrElse('spendCosts.totalSpend', a, 0))
		);
	};

	onSearch = (e) => {
		const { analytics } = this.props;
		const { spendCostsByProblemType } = analytics;

		const search = e.target.value;
		const allData = this.filterByProblemTypeId(
			nullSafeGetOrElse('data', spendCostsByProblemType, [])
		);

		this.setState({
			filteredTreeData: !!search
				? filterTreeData(allData, (obj) => obj.name.toLowerCase().includes(search.toLowerCase()))
				: [],
			search,
		});
	};

	removeProblemTypeIdsAndUpdateFilter = (filters) => {
		const { saveProblemTypeFilters } = this.props;
		const { problemTypeIds, ...otherFilters } = filters;
		this.setState({
			problemTypeIds,
		});
		saveProblemTypeFilters(otherFilters);
	};

	render() {
		const { clearAllProblemTypeFilters, analytics, accountActivated, currentUser, userType } =
			this.props;
		const { initialLoading, printing } = this.state;
		const { spendCostsByProblemType, spendCostsByMonth, problemTypeFilters } = analytics;
		const currency = getCurrency({ currentUser });

		const displayData = this.getDisplayData();
		// All node rows — drives active-source detection (dynamic columns) + CSV + the breakdown chart.
		const allFieldRows = flattenSpendCosts(displayData);
		const activeSources = getActiveSpendSources(allFieldRows);
		const printExpandedRowKeys = printing
			? collectAllParentsFromTreeData(displayData, (node) => node.id)
			: this.state.search
			? collectAllParentsFromTreeData(this.state.filteredTreeData, (node) => node.id)
			: null;

		const spendByProblemTypeColumns = [
			{
				title: 'Problem Type',
				dataIndex: 'name',
				key: 'name',
				width: '24%',
				render: (text) => (
					<Ellipsis tooltip={true} style={{ display: 'inline' }} length={28}>
						{text}
					</Ellipsis>
				),
			},
			...buildSpendBreakdownColumns({
				activeSources,
				currency,
				userType,
				baseFilters: problemTypeFilters,
				dimensionFilterKey: 'problemTypeIds',
				fromTree: true,
			}),
		];

		return (
			<Content
				className="analyticsOverviewProblemTypesPage"
				style={{ padding: '0 0.5em', position: 'relative' }}
			>
				<div className="analytics-pdf-only" style={{ display: 'none' }}>
					<h2>Spend Analytics – Problem Types</h2>
				</div>
				{accountActivated ? (
					<div>
						<Row key={1} style={{ margin: '0.5em -8px' }} gutter={16}>
							<Col span={24}>
								<div
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
									}}
								>
									<AnalyticsControlBar
										updateFilters={this.removeProblemTypeIdsAndUpdateFilter}
										problemTypeIds={this.state.problemTypeIds}
										clearFilters={clearAllProblemTypeFilters}
										locationIds={problemTypeFilters.locationIds}
										brandIds={problemTypeFilters.brandIds}
										startDate={problemTypeFilters.startDate}
										supplierFacilityIds={problemTypeFilters.supplierFacilityIds}
										woPriorityIds={problemTypeFilters.woPriorityIds}
										endDate={problemTypeFilters.endDate}
										isCapex={problemTypeFilters.isCapex}
										isPM={problemTypeFilters.isPM}
										workCreatedAtStartDate={problemTypeFilters.workCreatedAtStartDate}
										workCreatedAtEndDate={problemTypeFilters.workCreatedAtEndDate}
										workCompletedAtStartDate={problemTypeFilters.workCompletedAtStartDate}
										workCompletedAtEndDate={problemTypeFilters.workCompletedAtEndDate}
										areas={problemTypeFilters.areas}
										areaHierarchyNames={problemTypeFilters.areaHierarchyNames}
										showSearch
										search={this.state.search}
										onSearch={this.onSearch}
										showDownload={true}
										onDownload={this.handleExportCsv}
										showPrint={true}
										onPrint={this.handlePrint}
										filterConfig={{
											workCreatedAt: true,
											workCompletedAt: true,
											capEx: true,
											isSpendAnalytics: true,
											problemTypes: true,
											isPM: true,
											workOrderPriorities: true,
											[FILTER_CONFIG_NAMES.AREA]: true,
										}}
										searchPlaceholder={'Search Problem types'}
									/>
								</div>
							</Col>
						</Row>

						{spendCostsByMonth.loading || initialLoading || displayData.length > 0 ? (
							[
								<>
									<Row key={2} style={{ margin: '0.5em -8px' }} gutter={16}>
										<Col span={24}>
											<Card>
												<Spin spinning={spendCostsByProblemType.loading || initialLoading}>
													<Table
														pagination={printing ? false : undefined}
														columns={spendByProblemTypeColumns}
														rowKey={(el) => el['id']}
														dataSource={removeEmptyChildrenFromTreeData(displayData)}
														expandable={{
															...(printExpandedRowKeys && {
																expandedRowKeys: printExpandedRowKeys,
															}),
														}}
													/>
												</Spin>
											</Card>
										</Col>
									</Row>
									<Row>
										<Col span={24}>
											<SpendBreakdownSection
												loading={spendCostsByProblemType.loading || initialLoading}
												filters={problemTypeFilters}
												userType={userType}
												currency={currency}
												rows={allFieldRows}
												activeSources={activeSources}
												totalSources={SPEND_SOURCES}
												reportKey="spend_costs_by_problem_type_with_children"
												normalizeComparisonRows={(rawData) =>
													flattenSpendCosts(
														applyLocalAnalyticsTreeFilters(rawData || [], {
															selectedIds: this.state.problemTypeIds,
															search: this.state.search,
														})
													)
												}
												localFilterRevision={`${this.state.search}|${(
													this.state.problemTypeIds || []
												).join(',')}`}
												categoryLabel="Problem Type"
												titleLabel="Spend by Problem Type"
												csvTitle={CSV_TITLE}
												exportValueLabel="Total Spend"
											/>
										</Col>
									</Row>
								</>,

								<Row key={3} style={{ margin: '0.5em -8px' }} gutter={16}>
									<Col span={24}>
										<SpendOverTimeChart
											monthlyData={spendCostsByMonth.data}
											currency={currency}
											loading={spendCostsByMonth.loading || initialLoading}
										/>
									</Col>
								</Row>,
							]
						) : (
							<EmptyState
								height={undefined}
								headline={'No spend found'}
								body={
									<div style={{ maxWidth: 488, textAlign: 'center' }}>
										We didn't find any spend data. Try changing your filters or expanding the
										selected time period.
									</div>
								}
							/>
						)}
					</div>
				) : (
					<EmptyState
						graphic={
							<img
								alt="You haven't activated your account yet."
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/letter owl.svg"
							/>
						}
						headline={"You haven't activated your account yet."}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 560, marginBottom: 16 }}>
									Please check your email. We sent you a note with an activation link.
								</div>
							</div>
						}
					/>
				)}
			</Content>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,

	accountActivated: state.session.accountActivated,
	currentUser: state.session.currentUser,
	analytics: state.spend_analytics,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	saveProblemTypeFilters: (filters) => dispatch(updateProblemTypeFilters(filters)),
	clearAllProblemTypeFilters: (filters) => dispatch(clearProblemTypeFilters()),
	fetchSpendData: (params, filters = null, pagination = null, sorting = null) =>
		dispatch(
			fetchSpendCostsByProblemTypeWithChildren(ownProps.userType)(
				params,
				filters,
				pagination,
				sorting
			)
		),
	fetchTimeSeriesSpendData: (params, filters = null, pagination = null, sorting = null) =>
		dispatch(fetchSpendCostsByMonth(ownProps.userType)(params, filters, pagination, sorting)),
});

export default withUserType(
	withRouter(connect(mapStateToProps, mapDispatchToProps)(SpendAnalyticsOverviewProblemTypesPage))
);
